import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Clock3, ConciergeBell, X } from "lucide-react";
import type { RoomServiceStatus, RoomServiceTaskSummary } from "@apartos/contracts";
import { api } from "../lib/api";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";

const nextStatus: Partial<Record<RoomServiceStatus, RoomServiceStatus>> = {
  queued: "accepted", accepted: "preparing", preparing: "delivering", delivering: "completed",
};
const labels: Record<RoomServiceStatus, string> = {
  queued: "Принять", accepted: "Начать готовить", preparing: "Передать в доставку",
  delivering: "Доставлено", completed: "Выполнено", cancelled: "Отменено",
};

export function RoomServiceTaskModal({ task, onClose }: { task: RoomServiceTaskSummary; onClose: () => void }) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (status: RoomServiceStatus) => api.updateRoomService(task.id, { status }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["room-service"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["bookings"] }),
      ]);
      onClose();
    },
  });
  const next = nextStatus[task.status];
  const remaining = new Date(task.dueAt).getTime() - Date.now();
  const minutes = Math.ceil(Math.abs(remaining) / 60_000);
  const finished = task.status === "completed" || task.status === "cancelled";
  const timing = task.status === "completed" && task.completedAt
    ? `выполнено за ${Math.max(1, Math.round((new Date(task.completedAt).getTime() - new Date(task.createdAt).getTime()) / 60_000))} мин.`
    : task.status === "cancelled" ? "заказ отменён"
      : remaining < 0 ? `просрочено на ${minutes} мин.` : `осталось ${minutes} мин.`;
  return <ModalPortal onClose={onClose}><section ref={dialogRef} className="booking-modal" role="dialog" aria-modal="true" aria-labelledby="room-service-task-title">
    <header className="modal-header"><div><h2 id="room-service-task-title">Заказ · Apt {task.apartmentNumber}</h2><p>{task.guestName}</p></div><button className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17}/></button></header>
    <div className="modal-body"><div className={`task-detail-modal__lead ${remaining < 0 ? "tone-red" : remaining <= 10 * 60_000 ? "tone-yellow" : "tone-orange"}`}><span><ConciergeBell size={22}/></span><div><small>{task.status.toUpperCase()}</small><h3>{task.serviceName} × {task.quantity}</h3><p>{task.notes || "Без дополнительного комментария"}</p></div></div>
      <dl className="task-detail-modal__facts"><div><dt>{finished ? "Время" : "Срок"}</dt><dd><Clock3 size={13}/> {timing}</dd></div><div><dt>Исполнитель</dt><dd>{task.assignee || "Не назначен"}</dd></div><div><dt>Сумма</dt><dd>${task.total.toFixed(2)}</dd></div><div><dt>Создан</dt><dd>{new Date(task.createdAt).toLocaleString("ru-RU")}</dd></div></dl>
      {mutation.error && <div className="form-error"><CircleAlert size={15}/>{mutation.error.message}</div>}
    </div>
    <footer className="modal-footer"><button className="secondary-button" type="button" onClick={onClose}>Закрыть</button><div className="modal-footer__spacer"/>{next && <button className="primary-button" disabled={mutation.isPending} onClick={() => mutation.mutate(next)}>{labels[task.status]}</button>}</footer>
  </section></ModalPortal>;
}
